import { createStore } from "zustand/vanilla";
import { updateHistory } from "./api.ts";
import type {
  PairRequest,
  PairReconnect,
  Command,
  Pong,
  PairAccepted,
  PairRejected,
  StateUpdate,
  PlayerDisconnected,
  PlayerReconnected,
  Ping,
  RoomStatus as RoomStatusMsg,
  RoomStatusRequest,
  NameUpdate,
  RoomNameUpdate,
  ControllerInfo,
  SubtitleTrack,
  AudioTrack,
} from "../src/gen/player/relay/v1/relay.ts";
import type { TorrentStream, HistoryEntry } from "./api.ts";

const WS_URL = "wss://tvcode.link/ws/control";

function tracksEqual(a: { id: number; name: string }[], b: { id: number; name: string }[]): boolean {
  if (a.length !== b.length) return false;
  for (let i = 0; i < a.length; i++) {
    if (a[i]!.id !== b[i]!.id || a[i]!.name !== b[i]!.name) return false;
  }
  return true;
}

// --- Message types ---

type Typed<T, TType extends string> = Omit<T, "type"> & { type: TType };

type ControllerCommand = {
  type: "command";
  action: Command["action"];
  url?: string;
  title?: string;
  positionMs?: number;
  offsetMs?: number;
  level?: number;
  muted?: boolean;
  rate?: number;
  enabled?: boolean;
  subtitleTrackId?: number;
  subtitleDelayMs?: number;
  audioTrackId?: number;
  audioDelayMs?: number;
};

export type OutgoingMessage =
  | Typed<PairRequest, "pair:request">
  | Typed<PairReconnect, "pair:reconnect">
  | ControllerCommand
  | Typed<Pong, "pong">
  | Typed<RoomStatusRequest, "room:status:request">
  | Typed<NameUpdate, "name:update">
  | Typed<RoomNameUpdate, "room:name:update">;

type IncomingMessage =
  | Typed<PairAccepted, "pair:accepted">
  | Typed<PairRejected, "pair:rejected">
  | Typed<StateUpdate, "state:update">
  | Typed<PlayerDisconnected, "player:disconnected">
  | Typed<PlayerReconnected, "player:reconnected">
  | Typed<Ping, "ping">
  | Typed<RoomStatusMsg, "room:status">;

// --- Exported UI types ---

export type View = "search" | "player" | "history" | "settings";
export type PlayerStatus = "stopped" | "playing" | "paused" | "buffering";

export interface RoomInfo {
  roomName: string;
  playerOnline: boolean;
  controllers: ControllerInfo[];
}

// --- Module-level WebSocket state (not in store — doesn't drive rendering) ---

let ws: WebSocket | null = null;
let shouldReconnect = true;
let sessionToken: string | null = localStorage.getItem("sessionToken");
let lastPingAt = 0;
let heartbeatTimer: ReturnType<typeof setInterval> | null = null;
let reconnectTimer: ReturnType<typeof setTimeout> | null = null;
let connectTimeoutTimer: ReturnType<typeof setTimeout> | null = null;
const HEARTBEAT_INTERVAL_MS = 2_000;  // check every 2s
const PING_TIMEOUT_MS = 20_000;       // consider dead if no ping for 20s (server pings every 10s)
const CONNECT_TIMEOUT_MS = 10_000;    // give up on a connecting socket after 10s

function sendRaw(msg: OutgoingMessage) {
  if (ws && ws.readyState === WebSocket.OPEN) {
    ws.send(JSON.stringify(msg));
  }
}

function isConnectionHealthy(): boolean {
  if (!ws) return false;
  if (ws.readyState === WebSocket.CONNECTING) return true; // in-progress, give it time
  if (ws.readyState !== WebSocket.OPEN) return false;
  if (lastPingAt > 0 && Date.now() - lastPingAt > PING_TIMEOUT_MS) return false;
  return true;
}

function scheduleReconnect(delayMs: number) {
  if (reconnectTimer) { clearTimeout(reconnectTimer); reconnectTimer = null; }
  if (!shouldReconnect || !sessionToken) return;
  reconnectTimer = setTimeout(() => {
    reconnectTimer = null;
    tryReconnect();
  }, delayMs);
}

function tryReconnect() {
  if (!shouldReconnect || !sessionToken) return;
  // Don't interrupt an in-progress connection
  if (ws && (ws.readyState === WebSocket.OPEN || ws.readyState === WebSocket.CONNECTING)) return;
  // Cancel any pending scheduled reconnect — we're doing it now
  if (reconnectTimer) { clearTimeout(reconnectTimer); reconnectTimer = null; }
  connect({ type: "pair:reconnect", sessionToken: sessionToken! });
}

// --- Store ---

interface AppState {
  // View
  view: View;

  // Connection
  connected: boolean;
  pairing: boolean;
  pairError: string;

  // Player (flat)
  status: PlayerStatus;
  url: string;
  title: string;
  positionMs: number;
  durationMs: number;
  subtitleTracks: SubtitleTrack[];
  activeSubtitleTrackId: number | undefined;
  subtitleDelayMs: number;
  audioTracks: AudioTrack[];
  activeAudioTrackId: number | undefined;
  audioDelayMs: number;

  // Room
  roomStatus: RoomInfo | null;
  controllerName: string;

  // Actions
  send: (msg: OutgoingMessage) => void;
  pair: (code: string) => void;
  disconnect: () => void;
  requestRoomStatus: () => void;
  updateControllerName: (name: string) => void;
  updateRoomName: (name: string) => void;
  setTitle: (title: string, positionMs?: number, durationMs?: number) => void;
  setView: (view: View) => void;
  playStream: (stream: TorrentStream) => void;
  playFromHistory: (entry: HistoryEntry) => void;
}

function startHeartbeat() {
  stopHeartbeat();
  heartbeatTimer = setInterval(() => {
    if (!isConnectionHealthy()) {
      console.log("[WS] heartbeat: connection unhealthy, reconnecting");
      if (ws) { ws.onclose = null; ws.close(); }
      ws = null;
      appStore.setState({ connected: false });
      tryReconnect();
    }
  }, HEARTBEAT_INTERVAL_MS);
}

function stopHeartbeat() {
  if (heartbeatTimer) { clearInterval(heartbeatTimer); heartbeatTimer = null; }
}

function connect(pairMsg: Typed<PairRequest, "pair:request"> | Typed<PairReconnect, "pair:reconnect">) {
  // Cancel any pending reconnect — we're connecting now
  if (reconnectTimer) { clearTimeout(reconnectTimer); reconnectTimer = null; }

  if (ws) {
    ws.onclose = null;
    ws.close();
  }

  shouldReconnect = true;
  lastPingAt = 0;
  const socket = new WebSocket(WS_URL);
  ws = socket;

  // If the handshake doesn't complete in time, give up and retry
  connectTimeoutTimer = setTimeout(() => {
    connectTimeoutTimer = null;
    if (socket.readyState === WebSocket.CONNECTING) {
      console.log("[WS] connect timeout, retrying");
      socket.onclose = null;
      socket.close();
      ws = null;
      scheduleReconnect(2000);
    }
  }, CONNECT_TIMEOUT_MS);

  socket.onopen = () => {
    if (connectTimeoutTimer) { clearTimeout(connectTimeoutTimer); connectTimeoutTimer = null; }
    socket.send(JSON.stringify(pairMsg));
  };

  socket.onmessage = (e) => {
    let msg: IncomingMessage;
    try {
      msg = JSON.parse(e.data as string);
    } catch {
      return;
    }

    switch (msg.type) {
      case "pair:accepted":
        sessionToken = msg.sessionToken;
        localStorage.setItem("sessionToken", msg.sessionToken);
        if (msg.state) {
          appStore.setState({
            status: (msg.state.status as PlayerStatus) || "stopped",
            url: msg.state.url || "",
            title: msg.state.title || msg.state.url || "",
            positionMs: msg.state.positionMs || 0,
            durationMs: msg.state.durationMs || 0,
            subtitleTracks: msg.state.subtitleTracks || [],
            activeSubtitleTrackId: msg.state.activeSubtitleTrackId,
            subtitleDelayMs: msg.state.subtitleDelayMs || 0,
            audioTracks: msg.state.audioTracks || [],
            activeAudioTrackId: msg.state.activeAudioTrackId,
            audioDelayMs: msg.state.audioDelayMs || 0,
          });
        }
        appStore.setState({ pairing: false, pairError: "", connected: true });
        lastPingAt = Date.now();
        startHeartbeat();
        // Request room status and re-send controller name after successful pairing
        setTimeout(() => {
          sendRaw({ type: "room:status:request" });
          const name = localStorage.getItem("controllerName");
          if (name) sendRaw({ type: "name:update", name });
        }, 100);
        break;

      case "pair:rejected":
        localStorage.removeItem("sessionToken");
        sessionToken = null;
        appStore.setState({
          pairError:
            msg.reason === "invalid_code"
              ? "Invalid or expired code"
              : msg.reason === "invalid_session"
                ? "Session expired — enter code again"
                : msg.reason || "Rejected",
          pairing: false,
          connected: false,
          roomStatus: null,
        });
        break;

      case "state:update":
        if (msg.state) {
          console.log("[WS] state:update raw:", msg.state);
          const url = msg.state.url || "";
          const title = msg.state.title || msg.state.url || "";
          const status = (msg.state.status as PlayerStatus) || "stopped";
          const activeSubtitleTrackId = msg.state.activeSubtitleTrackId;
          const subtitleDelayMs = msg.state.subtitleDelayMs || 0;
          const activeAudioTrackId = msg.state.activeAudioTrackId;
          const audioDelayMs = msg.state.audioDelayMs || 0;

          // Preserve array references if tracks haven't changed to avoid unnecessary re-renders
          const incomingSubTracks = msg.state.subtitleTracks || [];
          const currentSubTracks = appStore.getState().subtitleTracks;
          const subtitleTracks = tracksEqual(currentSubTracks, incomingSubTracks) ? currentSubTracks : incomingSubTracks;

          const incomingAudioTracks = msg.state.audioTracks || [];
          const currentAudioTracks = appStore.getState().audioTracks;
          const audioTracks = tracksEqual(currentAudioTracks, incomingAudioTracks) ? currentAudioTracks : incomingAudioTracks;

          if (status === "playing" || status === "paused") {
            const positionMs = msg.state.positionMs || 0;
            const durationMs = msg.state.durationMs || 0;
            appStore.setState({ status, url, title, positionMs, durationMs, subtitleTracks, activeSubtitleTrackId, subtitleDelayMs, audioTracks, activeAudioTrackId, audioDelayMs });
            if (url && durationMs > 0) {
              updateHistory({ url, title, positionMs, durationMs });
            }
          } else {
            appStore.setState({ status, url, title, subtitleTracks, activeSubtitleTrackId, subtitleDelayMs, audioTracks, activeAudioTrackId, audioDelayMs });
          }
        }
        break;

      case "room:status":
        appStore.setState({
          roomStatus: {
            roomName: msg.roomName || "",
            playerOnline: msg.playerOnline,
            controllers: msg.controllers || [],
          },
        });
        break;

      case "ping":
        lastPingAt = Date.now();
        sendRaw({ type: "pong", ts: msg.ts });
        break;
    }
  };

  socket.onclose = () => {
    if (connectTimeoutTimer) { clearTimeout(connectTimeoutTimer); connectTimeoutTimer = null; }
    stopHeartbeat();
    appStore.setState({ connected: false });
    scheduleReconnect(2000);
  };
}

export const appStore = createStore<AppState>()((set, get) => ({
  // View
  view: "search",

  // Connection
  connected: false,
  pairing: false,
  pairError: "",

  // Player
  status: "stopped",
  url: "",
  title: "",
  positionMs: 0,
  durationMs: 0,
  subtitleTracks: [],
  activeSubtitleTrackId: undefined,
  subtitleDelayMs: 0,
  audioTracks: [],
  activeAudioTrackId: undefined,
  audioDelayMs: 0,

  // Room
  roomStatus: null,
  controllerName: localStorage.getItem("controllerName") || "",

  // Actions
  send: sendRaw,

  pair(code: string) {
    set({ pairError: "", pairing: true, roomStatus: null });
    connect({ type: "pair:request", code });
  },

  disconnect() {
    shouldReconnect = false;
    stopHeartbeat();
    if (reconnectTimer) { clearTimeout(reconnectTimer); reconnectTimer = null; }
    if (connectTimeoutTimer) { clearTimeout(connectTimeoutTimer); connectTimeoutTimer = null; }
    localStorage.removeItem("sessionToken");
    sessionToken = null;
    if (ws) {
      ws.onclose = null;
      ws.close();
    }
    ws = null;
    set({
      connected: false,
      roomStatus: null,
      pairError: "",
      status: "stopped",
      url: "",
      title: "",
      positionMs: 0,
      durationMs: 0,
      subtitleTracks: [],
      activeSubtitleTrackId: undefined,
      subtitleDelayMs: 0,
      audioTracks: [],
      activeAudioTrackId: undefined,
      audioDelayMs: 0,
    });
  },

  requestRoomStatus() {
    sendRaw({ type: "room:status:request" });
  },

  updateControllerName(name: string) {
    set({ controllerName: name });
    localStorage.setItem("controllerName", name);
    sendRaw({ type: "name:update", name });
  },

  updateRoomName(name: string) {
    sendRaw({ type: "room:name:update", name });
  },

  setTitle(title: string, positionMs?: number, durationMs?: number) {
    set({
      title,
      ...(positionMs != null && { positionMs }),
      ...(durationMs != null && { durationMs }),
    });
  },

  setView(view: View) {
    set({ view });
  },

  playStream(stream: TorrentStream) {
    if (!stream.url) return;
    sendRaw({ type: "command", action: "load", url: stream.url });
    get().setTitle(stream.title);
    set({ view: "player" });
  },

  playFromHistory(entry: HistoryEntry) {
    sendRaw({ type: "command", action: "load", url: entry.url, positionMs: entry.positionMs });
    get().setTitle(entry.title, entry.positionMs, entry.durationMs);
    set({ view: "player" });
  },
}));

// Auto-reconnect on load if session token exists
if (sessionToken) {
  connect({ type: "pair:reconnect", sessionToken });
}

// Reconnect when tab/app becomes visible again
document.addEventListener("visibilitychange", () => {
  if (document.visibilityState === "visible" && sessionToken && shouldReconnect) {
    if (!isConnectionHealthy()) {
      console.log("[WS] tab visible: connection unhealthy, reconnecting");
      if (ws) { ws.onclose = null; ws.close(); }
      ws = null;
      appStore.setState({ connected: false });
      tryReconnect();
    }
  }
});

// Reconnect when network comes back online
window.addEventListener("online", () => {
  if (sessionToken && shouldReconnect && !isConnectionHealthy()) {
    console.log("[WS] network online: reconnecting");
    if (ws) { ws.onclose = null; ws.close(); }
    ws = null;
    appStore.setState({ connected: false });
    tryReconnect();
  }
});
